import React, { useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    Stack,
    Tooltip,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button
} from '@mui/material';
import {
    ArrowForward as ArrowForwardIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { WorkflowStep, Party } from '../types';
import { getZoomCompensatedSelectMenuProps } from '../utils/zoomCompensation';

// --- Helper Components ---

export const StatusChip = ({ status, scale = 1 }: { status: string, scale?: number }) => {
    let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';

    switch (status?.toLowerCase()) {
        case 'approved':
        case 'validated':
            color = 'success';
            break;
        case 'rejected':
            color = 'error';
            break;
        case 'pending':
        case 'checking_pending':
        case 'approving_pending':
            color = 'warning';
            break;
        case 'issued':
            color = 'info';
            break;
        default:
            color = 'default';
    }

    const displayLabel = (status || 'Unknown').replace(/_/g, ' ').toUpperCase();

    return (
        <Tooltip title={displayLabel} arrow>
            <Chip
                label={displayLabel}
                size="small"
                color={color}
                variant="filled"
                sx={{
                    fontWeight: 500,
                    minWidth: 80 * scale,
                    height: 24 * scale,
                    fontSize: `${0.75 * scale}rem`,
                    '& .MuiChip-label': {
                        px: 1 * scale
                    }
                }}
            />
        </Tooltip>
    );
};

export const WorkflowSteps = ({ workflow, onEdit, scale = 1 }: { workflow: WorkflowStep[], onEdit?: (event: React.MouseEvent<HTMLElement>) => void, scale?: number }) => {
    if (!workflow || workflow.length === 0) {
        return (
            <Box onClick={onEdit} sx={{ cursor: onEdit ? 'pointer' : 'default', display: 'flex', alignItems: 'center', opacity: 0.6 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: `${0.75 * scale}rem` }}>No workflow defined</Typography>
                {onEdit && <EditIcon sx={{ fontSize: 14 * scale, ml: 1 }} />}
            </Box>
        );
    }

    return (
        <Stack
            direction="row"
            spacing={1 * scale}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ py: 0.5 * scale, cursor: onEdit ? 'pointer' : 'default' }}
            onClick={onEdit}
        >
            {workflow.map((step, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <ArrowForwardIcon fontSize="small" color="action" sx={{ opacity: 0.5, fontSize: 16 * scale }} />}
                    <Tooltip title={`User Name: ${step.party_name} Role: ${step.role === 'author' ? 'Originator' : step.role}${step.backup_party_name ? ` (Backup: ${step.backup_party_name})` : ''}`}>
                        <Chip
                            label={step.party_trigram}
                            size="small"
                            color={step.is_next ? 'secondary' : 'default'}
                            variant={step.is_next ? 'filled' : 'outlined'}
                            avatar={
                                <Box
                                    sx={{
                                        bgcolor: 'action.selected',
                                        color: 'text.primary',
                                        width: 20 * scale,
                                        height: 20 * scale,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 10 * scale,
                                        fontWeight: 'bold',
                                        ml: 0.5 * scale
                                    }}
                                >
                                    {step.role === 'author' ? 'O' : step.role.charAt(0).toUpperCase()}
                                </Box>
                            }
                            sx={{
                                height: 24 * scale,
                                '& .MuiChip-label': { px: 1 * scale, fontSize: `${0.75 * scale}rem` }
                            }}
                        />
                    </Tooltip>
                </React.Fragment>
            ))}
            {onEdit && (
                <IconButton size="small" sx={{ p: 0.5 * scale, ml: 1 * scale }}>
                    <EditIcon sx={{ fontSize: 14 * scale }} />
                </IconButton>
            )}
        </Stack>
    );
};

export const InlineWorkflowEditor = ({
    initialWorkflow,
    parties,
    onSave,
    onCancel
}: {
    initialWorkflow: WorkflowStep[],
    parties: Party[],
    onSave: (workflow: WorkflowStep[]) => void,
    onCancel: () => void
}) => {
    const [steps, setSteps] = useState<WorkflowStep[]>(initialWorkflow || []);
    const [selectedPartyId, setSelectedPartyId] = useState<number | ''>('');
    const [selectedRole, setSelectedRole] = useState<string>('checker');

    const handleAddStep = () => {
        if (!selectedPartyId) return;
        const party = parties.find(p => p.id === selectedPartyId);
        if (!party) return;

        const newStep: WorkflowStep = {
            party_name: party.name,
            backup_party_name: null,
            party_trigram: party.trigram,
            party_id: party.id,
            role: selectedRole,
            backup_party_trigram: null
        };

        setSteps([...steps, newStep]);
        setSelectedPartyId('');
        setSelectedRole('checker');
    };

    const handleRemoveStep = (index: number) => {
        const newSteps = [...steps];
        newSteps.splice(index, 1);
        setSteps(newSteps);
    };

    return (
        <Box sx={{ p: 2, width: 300, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Edit Workflow</Typography>

            <List dense sx={{ mb: 2, maxHeight: 150, overflow: 'auto', bgcolor: 'action.hover', borderRadius: 1 }}>
                {steps.map((step, index) => (
                    <ListItem key={index} divider={index < steps.length - 1}>
                        <ListItemText
                            primary={step.party_name}
                            secondary={step.role}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <ListItemSecondaryAction>
                            <IconButton edge="end" size="small" onClick={() => handleRemoveStep(index)}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
                {steps.length === 0 && (
                    <ListItem>
                        <ListItemText primary="No steps added" sx={{ fontStyle: 'italic', color: 'text.secondary' }} />
                    </ListItem>
                )}
            </List>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <FormControl size="small" fullWidth>
                    <InputLabel>Party</InputLabel>
                    <Select
                        value={selectedPartyId}
                        label="Party"
                        onChange={(e) => setSelectedPartyId(Number(e.target.value))}
                        MenuProps={getZoomCompensatedSelectMenuProps()}
                    >
                        {parties.map(p => (
                            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Role</InputLabel>
                    <Select
                        value={selectedRole}
                        label="Role"
                        onChange={(e) => setSelectedRole(e.target.value)}
                        MenuProps={getZoomCompensatedSelectMenuProps()}
                    >
                        <MenuItem value="author">Originator</MenuItem>
                        <MenuItem value="checker">Checker</MenuItem>
                        <MenuItem value="approver">Approver</MenuItem>
                    </Select>
                </FormControl>
                <IconButton color="primary" onClick={handleAddStep} disabled={!selectedPartyId}>
                    <AddIcon />
                </IconButton>
            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={1}>
                <Button size="small" onClick={onCancel}>Cancel</Button>
                <Button size="small" variant="contained" onClick={() => onSave(steps)}>Save</Button>
            </Stack>
        </Box>
    );
};
